import { useState, useRef, useEffect } from 'react';
import { FiEdit, FiTrash2, FiPlus, FiX } from 'react-icons/fi';
// import { useNavigate } from 'react-router-dom'; // Reserved for future navigation
import BlogForm from './BlogForm';
import { blogService } from '../services/apiService';

const BlogList = ({ blogs = [], fetchBlogs }) => {
  // const navigate = useNavigate(); // Reserved for future navigation
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const panelRef = useRef(null);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      setIsDeleting(true);
      try {
        await blogService.deleteBlog(id);
        fetchBlogs();
      } catch (error) {
        console.error('Error deleting blog:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  useEffect(() => {
    if (showForm && panelRef.current) {
      setTimeout(() => {
        panelRef.current.style.transform = 'translateX(0)';
      }, 10);
    }
  }, [showForm]);

  const closePanel = () => {
    if (panelRef.current) {
      panelRef.current.style.transform = 'translateX(100%)';
      setTimeout(() => {
        setShowForm(false);
      }, 300);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden relative">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Blog Posts</h3>
        <button
          onClick={() => {
            setSelectedBlog(null);
            setShowForm(true);
          }}
          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-[#166534] hover:bg-[#0e3d1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534]"
        >
          <FiPlus className="mr-1" /> Create Blog
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Thumbnail
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Author
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Publish Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tags
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {blogs.map((blog) => (
              <tr key={blog._id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  {blog.thumbnail && (
                    <img 
                      src={`data:image/jpeg;base64,${blog.thumbnail}`} 
                      alt="Thumbnail" 
                      className="h-12 w-12 object-cover rounded"
                    />
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{blog.title}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{blog.author}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {new Date(blog.publishDate).toLocaleDateString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-wrap gap-1">
                    {blog.tags?.map((tag, index) => (
                      <span key={index} className="px-2 py-1 text-xs rounded-full bg-[#166534]/10 text-[#0e3d1f]">
                        {tag}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => {
                      setSelectedBlog(blog);
                      setShowForm(true);
                    }}
                    className="text-[#166534] hover:text-[#0e3d1f] mr-3"
                  >
                    <FiEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(blog._id)}
                    disabled={isDeleting}
                    className="text-red-500 hover:text-red-700"
                  >
                    <FiTrash2 />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sliding Panel */}
      {showForm && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            <div 
              className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
              onClick={closePanel}
            />
            
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
              <div
                ref={panelRef}
                className="relative w-screen max-w-md transform transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
                style={{ 
                  transform: 'translateX(calc(100% + 20px))',
                  marginLeft: '-20px'
                }}
              >
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
                  <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {selectedBlog ? 'Edit Blog' : 'Create New Blog'}
                    </h3>
                    <button
                      onClick={closePanel}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none transition-colors duration-200"
                    >
                      <FiX className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="flex-1 p-4">
                    <BlogForm
                      blog={selectedBlog}
                      onClose={closePanel}
                      onSave={() => {
                        fetchBlogs();
                        closePanel();
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogList;